/**
 * threadSummary — pure builder that turns a referenced invoice/receipt
 * Firestore document into a {@link MessageThreadContextSummary} for the
 * Kontext-Band header of {@link MessageThreadView}.
 *
 * The message center (`MessagesPage`) — unlike the row-level buttons in
 * `InvoiceArchive`/`ReceivedInvoiceCard`/`ReceiptScanPage` — does not already
 * have the referenced row loaded, so it resolves the document by id and feeds
 * the raw `DocumentData` here. This function reproduces the EXACT field/format
 * mapping the row buttons use (verified against those files), so a thread shows
 * the same human title, subtitle, amount, badge and jump-link everywhere.
 *
 * It is deliberately pure (no i18n runtime, no Firestore access): all
 * translatable one-off labels are passed in via {@link ThreadSummaryLabels},
 * which keeps it trivially unit-testable and free of side effects. Foreign
 * documents are UNTRUSTED input: every field is runtime-narrowed and tolerated
 * when missing (never throws).
 *
 * @module threadSummary
 */

import { MessageContextType, ReceiptRefCollection } from '../../types/messagingTypes';
import { MessageThreadContextSummary } from './MessageThreadView';
import { formatCurrency, formatDate as formatDeDate } from '../../utils/formatters';

/** Loosely-typed foreign document as read from Firestore (`DocumentData`-shaped). */
export type ThreadDocData = Record<string, unknown>;

/** One-off translated labels supplied by the caller (keeps this module pure). */
export interface ThreadSummaryLabels {
  /** Fallback buyer name for invoices without `buyerName` (InvoiceArchive: `archive.table.values.unknown`). */
  unknownBuyer: string;
  /** Badge label for ZUGFeRD invoices (InvoiceArchive: `archive.filter.zugferd`). */
  zugferd: string;
  /** Badge label for XRechnung invoices (InvoiceArchive: `archive.filter.xrechnung`). */
  xrechnung: string;
}

/** Narrows an unknown value to a non-empty string, else `undefined`. */
function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Narrows an unknown value to a finite number, else `undefined`. */
function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Minimal shape of a Firestore `Timestamp` (only `toDate` is needed here). */
interface TimestampLike {
  toDate: () => Date;
}

function isTimestampLike(value: unknown): value is TimestampLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  );
}

/**
 * Converts a Firestore `Timestamp`, ISO string or millis number to a valid
 * `Date`, or `undefined` when the input is absent/unparseable.
 */
function toDateMaybe(value: unknown): Date | undefined {
  if (isTimestampLike(value)) {
    try {
      const date = value.toDate();
      return Number.isNaN(date.getTime()) ? undefined : date;
    } catch {
      return undefined;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
}

/**
 * Receipt date formatter — delegates to the central {@link formatDeDate}
 * (`utils/formatters.ts::formatDate`) for a deterministic, locale-fähiges
 * `DD.MM.YYYY` (never the raw, options-less `toLocaleDateString` which
 * produced non-zero-padded dates like `6.2.2026`). Missing/unparseable
 * input already resolves to the app-wide `'—'` fallback.
 *
 * @param value - Raw Firestore field (Timestamp/ISO string/millis/…).
 * @param locale - i18n language code, see {@link FormatDateOptions}. Default `'de'`.
 */
function formatReceiptDate(value: unknown, locale: string = 'de'): string {
  const date = toDateMaybe(value);
  return date ? formatDeDate(date, { locale }) : '—';
}

/**
 * Builds the context-band summary for a resolved invoice/receipt document.
 *
 * Field/format mapping (1:1 with the row-level buttons):
 * - `invoice`  → `InvoiceArchive` (`invoices/{refId}`):
 *   title `invoiceNumber||refId`, subtitle `${buyerName||unknownBuyer} · ${date}`,
 *   amount `totalAmount.toFixed(2) currency`, badge from `type`,
 *   href `/dashboard/archiv?id=${refId}`.
 * - `receipt` + `received_invoices` → `ReceivedInvoiceCard` (`received_invoices/{refId}.data`):
 *   title `documentNo||refId`, subtitle `[sellerName, date].filter(Boolean).join(' · ')`,
 *   amount `formatCurrency(grandTotal, currency)`, href `/dashboard/eingangsrechnungen`.
 * - `receipt` + `scanned_receipts` → `ReceiptScanPage` (`scanned_receipts/{refId}`):
 *   title `originalFilename||refId`, subtitle `extraction.supplierName`,
 *   href `/dashboard/belege`.
 *
 * @param type - The thread's context type.
 * @param refCollection - Receipt sub-collection (only meaningful for receipts).
 * @param refId - Firestore doc id of the referenced document.
 * @param docData - Raw (untrusted) document data resolved from Firestore.
 * @param labels - Translated one-off labels (see {@link ThreadSummaryLabels}).
 * @param locale - i18n language code for date formatting (see {@link FormatDateOptions}). Default `'de'`.
 * @returns A summary for the Kontext-Band, or `undefined` for `general` /
 *          unsupported combinations (caller then keeps its refId fallback title).
 */
export function buildThreadSummary(
  type: MessageContextType,
  refCollection: ReceiptRefCollection | null,
  refId: string,
  docData: ThreadDocData,
  labels: ThreadSummaryLabels,
  locale: string = 'de',
): MessageThreadContextSummary | undefined {
  if (type === 'invoice') {
    const invoiceNumber = asString(docData.invoiceNumber);
    const buyerName = asString(docData.buyerName) ?? labels.unknownBuyer;
    const createdAt = toDateMaybe(docData.createdAt);
    const datePart = createdAt ? formatDeDate(createdAt, { locale }) : '';
    const totalAmount = asNumber(docData.totalAmount);
    const currency = asString(docData.currency) ?? 'EUR';
    const invoiceType = docData.type === 'zugferd' || docData.type === 'xrechnung' ? docData.type : undefined;

    return {
      title: invoiceNumber ?? refId,
      subtitle: datePart ? `${buyerName} · ${datePart}` : buyerName,
      amountLabel: totalAmount !== undefined ? `${totalAmount.toFixed(2)} ${currency}` : undefined,
      badgeLabel: invoiceType ? (invoiceType === 'zugferd' ? labels.zugferd : labels.xrechnung) : undefined,
      badgeVariant: invoiceType,
      hrefToRef: `/dashboard/archiv?id=${refId}`,
    };
  }

  if (type === 'receipt' && refCollection === 'received_invoices') {
    // `received_invoices` nests its parsed fields under `data` (ParsedZugferdData).
    const data = (typeof docData.data === 'object' && docData.data !== null ? docData.data : {}) as ThreadDocData;
    const documentNo = asString(data.documentNo);
    const sellerName = asString(data.sellerName);
    const currency = asString(data.currency) ?? 'EUR';
    const grandTotal = asNumber(data.grandTotal);
    const subtitle = [sellerName, formatReceiptDate(data.documentDate, locale)].filter(Boolean).join(' · ');

    return {
      title: documentNo ?? refId,
      subtitle: subtitle.length > 0 ? subtitle : undefined,
      amountLabel: grandTotal !== undefined ? formatCurrency(grandTotal, currency) : undefined,
      badgeVariant: 'receipt',
      hrefToRef: '/dashboard/eingangsrechnungen',
    };
  }

  if (type === 'receipt' && refCollection === 'scanned_receipts') {
    const originalFilename = asString(docData.originalFilename);
    const extraction =
      typeof docData.extraction === 'object' && docData.extraction !== null
        ? (docData.extraction as ThreadDocData)
        : undefined;
    const supplierName = extraction ? asString(extraction.supplierName) : undefined;

    return {
      title: originalFilename ?? refId,
      subtitle: supplierName,
      badgeVariant: 'receipt',
      hrefToRef: '/dashboard/belege',
    };
  }

  return undefined;
}
